import type { ApiTechnicalDataSheet, ApiTechnicalValue } from '@dh-io-owpi/backend-api/src/data-contracts';
import type { TableData, TableGroup, TableHeader, TableRow } from './types';
import { type TechDataWltp } from '@dh-io-owpi/spa-configuration';

export type FuelFilter = {
  label: string;
  models: string[];
};

export type LabeledTableData = TableData & { label: string };

type IndexedTableRow = TableRow<any> & { _s: number };

export function mapTechData(
  technicalDataSheet: ApiTechnicalDataSheet,
  techDataWltpConfig: TechDataWltp,
  fuelFilterValue: FuelFilter[],
  ecoLabel: string,
): LabeledTableData[] {
  const { models, footnotes } = technicalDataSheet;
  const showWltp = techDataWltpConfig.enabled === 'body' || techDataWltpConfig.enabled === true;

  return fuelFilterValue.map<LabeledTableData>((m) => {
    const headers: TableHeader[] = [];

    const group: Record<string, IndexedTableRow> = {};
    let sortId = 0;
    for (const model of m.models) {
      const { label, keyFacts } = models[model] ?? {};
      if (!keyFacts.length) continue;
      headers.push({ title: label });
      keyFacts.forEach((v) => {
        addTechnicalValueToGroup(group, v, sortId++, footnotes);

        if (showWltp) addEcoLabelToGroup(techDataWltpConfig, group, v, ecoLabel);
      });
    }

    return {
      label: m.label,
      groups: [{ rows: Object.values(group).sort((a, b) => a._s - b._s) }],
      headers,
    };
  });
}

export function mapAllTechData(
  technicalDataSheet: ApiTechnicalDataSheet,
  techdataWltpConfig: TechDataWltp,
  fuelFilterValue: FuelFilter[],
  ecoLabel: string,
): LabeledTableData[] {
  const { models, footnotes } = technicalDataSheet;
  const showWltp = techdataWltpConfig.enabled === 'modal' || techdataWltpConfig.enabled === true;

  return fuelFilterValue.map<LabeledTableData>((m) => {
    const groupMap: Record<string, Record<string, IndexedTableRow>> = {};
    const headers: TableHeader[] = [];

    for (const model of m.models) {
      const { label, technicalValueGroups } = models[model];
      if (!technicalValueGroups.length) continue;
      headers.push({ title: label });

      for (const g of technicalValueGroups) {
        let sortId = 0;
        for (const v of g.technicalValues) {
          const group = (groupMap[g.label] ??= {});
          addTechnicalValueToGroup(group, v, sortId++, footnotes);

          if (showWltp) addEcoLabelToGroup(techdataWltpConfig, group, v, ecoLabel);
        }
      }
    }

    const groups: TableGroup[] = Object.entries(groupMap).map(([label, rows]) => ({
      label,
      // sort rows by the sortId (_s) to ensure that rows appear the same order as come from BE and that WLTP is first
      rows: Object.values(rows).sort((a, b) => a._s - b._s),
    }));

    return { label: m.label, headers, groups } satisfies LabeledTableData;
  });
}

function addTechnicalValueToGroup(
  group: Record<string, IndexedTableRow>,
  v: ApiTechnicalValue,
  _s: number,
  footnotes: Record<string, string>,
) {
  const row = (group[v.id] ??= {
    _s,
    id: v.id,
    label: v.label,
    values: [],
    description: v.description,
  });
  row.values.push({ value: v.value, footnotes: v.footnotes?.map((id) => footnotes[id]) });
}

/** custom id we use to identify our custom row for the wltp eco rating */
export const wltpTableRowId = 'owpi.wltp';

function addEcoLabelToGroup(
  techdataWltpConfig: TechDataWltp,
  group: Record<string, IndexedTableRow>,
  v: ApiTechnicalValue,
  ecoLabel: string,
) {
  if (techdataWltpConfig.ratingId === v.id) {
    const wltpRow = (group[wltpTableRowId] ??= { _s: -1, id: wltpTableRowId, label: ecoLabel, values: [] });

    const emission = v.subvalues?.find((sv) => techdataWltpConfig.emissionId === sv.id)?.value;
    // add a table value but instead of a string it will be {rating: string, emission?: string}
    wltpRow.values.push({ value: { rating: v.value, emission } });
  }
}

function areAllValuesEmpty(rows: TableRow<string>[]) {
  return rows.filter((r) => !r.values.every((value) => value.value === '-'));
}

// When all the values on the row are empty "-", we remove the row
export function removeEmptyRows(data: LabeledTableData[]): LabeledTableData[] {
  return data.map((d) => ({
    ...d,
    groups: d.groups.map((g) => ({
      ...g,
      rows: areAllValuesEmpty(g.rows),
    })),
  }));
}

// When all the rows in a group are empty, we remove the group
export function removeEmptyRowsAndGroups(data: LabeledTableData[]): LabeledTableData[] {
  const filteredData = removeEmptyRows(data);

  return filteredData.map((d) => {
    const filteredGroups = d.groups.filter((g) => g.rows.length > 0);
    return { ...d, groups: filteredGroups };
  });
}
